import { useLayoutEffect } from "react";
import { findScrollParent } from "./useFillViewportHeight";
import { alleFenster } from "../components/windowManager/browserFenster/fensterRegister";

/**
 * Misst den Platz, den klebende Kopfleisten ÜBER einem Element dauerhaft belegen,
 * und legt ihn als CSS-Variablen am Element ab — damit ein `position: sticky`
 * INNERHALB des Elements sich darunter einhängen kann, statt darunter zu
 * verschwinden.
 *
 * Warum überhaupt gemessen wird, statt eine Zahl hinzuschreiben: Der Kontaktkopf
 * (`.dhead`) ist je nach Kontakt unterschiedlich hoch (Titel bricht um, Chip-Reihe
 * wächst) und klappt sich unter 768px beim Scrollen selbst zusammen
 * (`.kopf-kompakt`, T-146). Ein fester Offset wäre auf
 * dem einen Kontakt zu klein (der Tabellenkopf verschwände) und auf dem anderen zu
 * groß (eine Lücke klaffte).
 *
 * Gesetzt werden zwei Variablen:
 * - `--kt-head-top`: Unterkante der fremden Kopfleisten. Darauf klebt die eigene
 *   Werkzeugleiste.
 * - `--kt-thead-top`: dieselbe Kante plus die Höhe der eigenen Werkzeugleiste.
 *   Darauf klebt der Tabellenkopf.
 *
 * @param ref      Wurzel des Reiters; hier landen die Variablen.
 * @param headRef  Die eigene, ebenfalls klebende Werkzeugleiste innerhalb der Wurzel.
 */
export function useStickyStackOffset(
  ref: React.RefObject<HTMLElement>,
  headRef: React.RefObject<HTMLElement>,
): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined") return;

    const apply = () => {
      const scroller = findScrollParent(el);
      const oben = stickyAbove(el, scroller);
      const eigene = headRef.current?.getBoundingClientRect().height ?? 0;
      // AUFRUNDEN, nicht runden: Bruchteile eines Pixels (hier 230,5) sind der
      // Normalfall. Zu klein bedeutet, dass die Leiste darüber die obere Kante
      // der darunter verdeckt — sichtbar. Zu groß bedeutet einen halben Pixel
      // Luft — unsichtbar. Der Fehler darf also nur in eine Richtung gehen.
      el.style.setProperty("--kt-head-top", `${Math.ceil(oben)}px`);
      el.style.setProperty("--kt-thead-top", `${Math.ceil(oben + eigene)}px`);
    };

    apply();
    // Zweiter Lauf im nächsten Frame: Schriften und die Chip-Reihe des Kopfes
    // stehen beim ersten Layout noch nicht in ihrer endgültigen Höhe.
    const frame = window.requestAnimationFrame(apply);

    alleFenster.addEventListener("resize", apply);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(apply) : null;
    if (ro) {
      /**
       * `box: "border-box"` ist hier NICHT optional.
       *
       * Ein ResizeObserver meldet standardmäßig nur Änderungen der INHALTSBOX.
       * Der Kontaktkopf trägt aber einen 2px-Goldrahmen unten, und der zählt dort
       * nicht mit: wächst allein die Außenhöhe, feuert der Beobachter nie und der
       * Offset bleibt um genau diese 2px zu klein stehen — der Kopf verdeckt dann
       * die obere Kante der Werkzeugleiste. Auf DEV im ausgelieferten Build
       * gemessen: Variable 228,5px bei tatsächlich 230,5px Außenhöhe.
       *
       * Beobachtet wird die ganze Kette bis zum Scroller: der Kopf wächst, wenn
       * ein Chip umbricht, und die eigene Werkzeugleiste wird zweizeilig, sobald
       * der „Speichern"-Knopf dazukommt.
       */
      const beobachten = (n: HTMLElement) => ro.observe(n, { box: "border-box" });
      if (headRef.current) beobachten(headRef.current);
      const scroller = findScrollParent(el);
      let node: HTMLElement | null = el.parentElement;
      while (node) {
        beobachten(node);
        for (const geschwister of Array.from(node.children)) {
          if (geschwister !== node) beobachten(geschwister as HTMLElement);
        }
        if (node === scroller || node === document.body) break;
        node = node.parentElement;
      }
    }

    return () => {
      window.cancelAnimationFrame(frame);
      alleFenster.removeEventListener("resize", apply);
      ro?.disconnect();
    };
  }, [ref, headRef]);
}

/**
 * Höhe der klebenden Kopfleisten oberhalb von `el` bis zum Scroll-Container.
 *
 * Spiegelbild zu `reservedBelow` in `useFillViewportHeight`: dort wird nach unten
 * summiert, hier nach oben gesucht. Betrachtet werden die VORANGEHENDEN
 * Geschwister auf jeder Ebene — und, falls ein Geschwister selbst nicht klebt,
 * dessen klebende Nachfahren (z. B. eine klebende Leiste in einem statischen Block).
 *
 * Zurückgegeben wird das MAXIMUM aus `top + Höhe`, nicht die Summe: mehrere
 * klebende Leisten mit `top: 0` liegen übereinander, nicht hintereinander.
 */
function stickyAbove(el: HTMLElement, scroller: HTMLElement | null): number {
  let max = 0;
  let node: HTMLElement = el;
  while (node.parentElement) {
    const parent = node.parentElement;
    for (let sib = node.previousElementSibling; sib; sib = sib.previousElementSibling) {
      max = Math.max(max, pinnedBottom(sib as HTMLElement));
    }
    if (parent === scroller || parent === document.body) break;
    node = parent;
  }
  return max;
}

/** Unterkante eines klebenden Elements im angehefteten Zustand (0, wenn es nicht klebt). */
function pinnedBottom(el: HTMLElement): number {
  const stil = window.getComputedStyle(el);
  if (stil.position === "sticky" && stil.top !== "auto") {
    return (parseFloat(stil.top) || 0) + el.getBoundingClientRect().height;
  }
  // Nicht selbst klebend → in die Tiefe schauen. `querySelectorAll` bleibt hier
  // billig, weil nur die wenigen Geschwister OBERHALB des Reiters betrachtet
  // werden, nicht der Reiter selbst.
  let max = 0;
  for (const kind of Array.from(el.querySelectorAll<HTMLElement>("*"))) {
    const kindStil = window.getComputedStyle(kind);
    if (kindStil.position === "sticky" && kindStil.top !== "auto") {
      max = Math.max(max, (parseFloat(kindStil.top) || 0) + kind.getBoundingClientRect().height);
    }
  }
  return max;
}
